/**
 * The transcript page's Share button (SEO_BRIEF_11 section 5).
 *
 * The markup is the server's (web/templates/app/_share.html): a `<details>` whose summary is the
 * Share button and whose panel is the menu: Copy link, then WhatsApp, Telegram, X and Email as
 * plain links the server already filled with the page's own address. Without this module the
 * menu still opens and its four links still work; only Copy link needs a script.
 *
 *   * ON A PHONE (a coarse primary pointer, with `navigator.share`), a tap opens the device's own
 *     share sheet with the page's title and address, and the menu stays shut. The sheet is the
 *     brief's phone behaviour; a desktop browser that also has `navigator.share` gets the menu,
 *     which is the brief's desktop behaviour (`useNativeShare`). A sheet the visitor closes is
 *     nothing; one that fails for any other reason opens the menu instead, so the button is
 *     never dead.
 *   * THE MENU closes on Escape (focus back on the button), on a tap outside it, and once an
 *     entry is used. Copy link puts the address on the clipboard and says so in a toast; where
 *     the clipboard is refused, the address shows in a read-only field, selected, for the
 *     visitor to copy.
 *   * COUNTED (lib/usage.ts): one `share` per link shared, with how. The share sheet counts once
 *     the visitor picked where to send it (the promise resolved), never when it was closed.
 *
 * Every word on screen is the template's; this module carries none.
 */

import { copyText } from './clipboard.ts';
import { data, on, qs, qsa } from './dom.ts';
import { toast } from './toast.ts';
import { countUse, SHARE_METHODS, type ShareMethod } from './usage.ts';

/** The usage counter's tool for the transcript page (pages/transcript.ts). */
const USAGE_TOOL = 'transcript';
/** A transcript is text: the format the page's events are counted under. */
const USAGE_FORMAT = 'txt';

/** The two parts of `navigator` this module reads, so a test can hand in its own. */
export interface ShareNavigator {
  share?: (data: ShareData) => Promise<void>;
  canShare?: (data: ShareData) => boolean;
}

/**
 * Whether a tap opens the device's share sheet rather than the menu: a browser with
 * `navigator.share` that takes this page's title and address, on a device whose primary pointer is
 * a finger. Chrome on Windows and Safari on a Mac have `navigator.share` too, and there the menu
 * (Copy link first) is what the brief asks for.
 */
export function useNativeShare(
  nav: ShareNavigator | undefined,
  shareData: ShareData,
  coarsePointer: boolean,
): boolean {
  if (!coarsePointer || nav === undefined || typeof nav.share !== 'function') return false;
  if (typeof nav.canShare !== 'function') return true;
  try {
    return nav.canShare(shareData);
  } catch {
    return false;
  }
}

/** True for a share sheet the visitor closed without picking anything: not a failure. */
export function wasCancelled(err: unknown): boolean {
  return (
    typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AbortError'
  );
}

/** A menu entry's `data-share-method` as one of SHARE_METHODS, or null. */
export function shareMethod(value: string | null): ShareMethod | null {
  return value !== null && (SHARE_METHODS as readonly string[]).includes(value)
    ? (value as ShareMethod)
    : null;
}

/** The page's title and address as the share sheet takes them; no title when there is none. */
export function shareDataFor(url: string, title: string | null): ShareData {
  return title ? { title, url } : { url };
}

/** Count one link shared, and how. Never throws (lib/usage.ts). */
export function countShare(method: ShareMethod): void {
  countUse('share', USAGE_TOOL, USAGE_FORMAT, { method });
}

function coarsePointer(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/** Wire the Share button the server rendered in `root`, if it rendered one. */
export function mountShare(root: ParentNode = document): void {
  const box = qs<HTMLElement>('[data-share]', root);
  if (!box) return;
  const url = data(box, 'share-url');
  const menu = qs<HTMLDetailsElement>('[data-share-menu]', box);
  const button = qs<HTMLElement>('[data-share-button]', box);
  if (!url || !menu || !button) return;
  const linkField = qs<HTMLInputElement>('[data-share-link]', box);
  const shareData = shareDataFor(url, data(box, 'share-title'));
  const nav = (typeof navigator === 'undefined' ? undefined : navigator) as
    | ShareNavigator
    | undefined;

  const close = (refocus: boolean): void => {
    if (!menu.open) return;
    menu.open = false;
    if (refocus) button.focus();
  };

  on(button, 'click', (ev) => {
    // A tap on the open menu's button closes it: the element does that by itself.
    if (menu.open || !useNativeShare(nav, shareData, coarsePointer())) return;
    ev.preventDefault();
    const share = nav?.share;
    if (typeof share !== 'function') return;
    // No await before the call: the sheet needs the tap's activation, which Safari drops
    // across one.
    share.call(nav, shareData).then(
      () => countShare('native'),
      (err: unknown) => {
        if (!wasCancelled(err)) menu.open = true;
      },
    );
  });

  for (const item of qsa<HTMLElement>('[data-share-method]', box)) {
    const method = shareMethod(data(item, 'share-method'));
    if (method === null) continue;
    on(item, 'click', () => {
      if (method !== 'copy') {
        countShare(method);
        // After the link's own navigation has started, so closing cannot get in its way.
        setTimeout(() => close(false), 0);
        return;
      }
      void copyText(url).then((ok) => {
        if (ok) {
          toast(data(item, 'copied-message') ?? '');
          countShare('copy');
          close(true);
          return;
        }
        if (linkField) {
          linkField.hidden = false;
          linkField.focus();
          linkField.select();
        }
      });
    });
  }

  document.addEventListener('click', (ev) => {
    const target = ev.target;
    if (menu.open && target instanceof Node && !menu.contains(target)) close(false);
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && menu.open) close(true);
  });
}
